import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../../core/context/AuthContext";
import { useMeetNotesOffer } from "./shared/useMeetNotesOffer";
import {
  markWhatsNewSeen,
  readSeenWhatsNew,
  unseenWhatsNewIds,
  visibleReleases,
} from "./shared/whatsNew";

export const WhatsNewContext = createContext(null);

/**
 * One source of truth for What's new, shared by the account menu (inside the sidebar) and the
 * sidebar toggle (which stays visible when the sidebar is closed), so both show the same
 * purple dot and opening the pane clears it everywhere.
 *
 * @example
 * <WhatsNewProvider><BusinessModeLayoutContent /></WhatsNewProvider>
 */
export const WhatsNewProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id || user?.email || "";
  const meet = useMeetNotesOffer(userId);
  const [seenIds, setSeenIds] = useState(() => readSeenWhatsNew(userId));

  useEffect(() => {
    setSeenIds(readSeenWhatsNew(userId));
  }, [userId]);

  // Each action entry stays listed whatever the user answered, so there is always somewhere to
  // go: Gemini notes for anyone with a Google Meet connector, quick capture for everyone.
  const activeIds = ["quick-capture"];
  if (meet.hasMeetConnector) activeIds.unshift("meet-gemini-notes");
  const releases = visibleReleases({ activeIds });
  const unseenIds = unseenWhatsNewIds(releases, seenIds);

  const markSeen = useCallback(
    (ids) => setSeenIds(markWhatsNewSeen(userId, ids)),
    [userId]
  );

  const value = {
    userId,
    meet,
    releases,
    unseenIds,
    hasUnseen: unseenIds.length > 0,
    markSeen,
  };
  return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>;
};

/**
 * @example
 * const { hasUnseen } = useWhatsNew(); // null outside a WhatsNewProvider
 */
export const useWhatsNew = () => useContext(WhatsNewContext);
